import { useState, useEffect } from "react";
import axios from "axios";
import {
  User,
  Plus,
  Pencil,
  MagnifyingGlass,
  Phone,
  Storefront,
  X,
  IdentificationCard,
  Prohibit,
  ArrowCounterClockwise,
} from "@phosphor-icons/react";
import Layout from "../components/Layout";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function TechniciansPage() {
  const [technicians, setTechnicians] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTech, setEditingTech] = useState(null);
  const [search, setSearch] = useState("");
  const [filterStore, setFilterStore] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    cpf: "",
    phone: "",
    store_id: "",
  });

  useEffect(() => {
    fetchData();
  }, [filterStore]);

  const fetchData = async () => {
    try {
      const [techRes, storesRes] = await Promise.all([
        axios.get(
          `${API}/technicians?active_only=false${filterStore ? `&store_id=${filterStore}` : ""}`,
          { withCredentials: true },
        ),
        axios.get(`${API}/stores?active_only=false`, { withCredentials: true }),
      ]);
      setTechnicians(techRes.data);
      setStores(storesRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const data = {
        ...formData,
        store_id: formData.store_id || null,
      };

      if (editingTech) {
        await axios.put(`${API}/technicians/${editingTech.id}`, data, {
          withCredentials: true,
        });
        toast.success("Técnico atualizado com sucesso!");
      } else {
        await axios.post(`${API}/technicians`, data, { withCredentials: true });
        toast.success("Técnico cadastrado com sucesso!");
      }

      setShowForm(false);
      setEditingTech(null);
      setFormData({ name: "", cpf: "", phone: "", store_id: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar técnico");
    }
  };

  const handleEdit = (tech) => {
    setEditingTech(tech);
    setFormData({
      name: tech.name || "",
      cpf: tech.cpf || "",
      phone: tech.phone || "",
      store_id: tech.store_id || "",
    });
    setShowForm(true);
  };

  const handleToggleActive = (item) => {
    const isCurrentlyActive = item.active !== false;
    const actionText = isCurrentlyActive ? "desativar" : "reativar";

    toast(`${isCurrentlyActive ? "Desativar" : "Reativar"} técnico?`, {
      description: `Deseja realmente ${actionText} "${item.name}"?`,
      action: {
        label: `Sim, ${actionText}`,
        onClick: async () => {
          try {
            if (isCurrentlyActive) {
              await axios.delete(`${API}/technicians/${item.id}`, {
                withCredentials: true,
              });
            } else {
              await axios.put(
                `${API}/technicians/${item.id}`,
                { active: true },
                { withCredentials: true },
              );
            }
            toast.success(
              `Técnico ${isCurrentlyActive ? "desativado" : "reativado"}!`,
            );
            fetchData();
          } catch (error) {
            toast.error(`Erro ao ${actionText}`);
          }
        },
      },
      cancel: { label: "Cancelar" },
      duration: 5000,
    });
  };

  const filteredTechs = technicians.filter(
    (t) =>
      t.name.toLowerCase().includes(search.toLowerCase()) ||
      (t.cpf && t.cpf.includes(search)),
  );

  if (loading) {
    return (
      <Layout title="Técnicos">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-2 border-[#00E676] border-t-transparent rounded-full"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Técnicos">
      <div className="space-y-4 pb-6">
        {/* Search */}
        <div className="relative w-full">
          <MagnifyingGlass
            size={20}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A1A1AA] shrink-0"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-field !pl-12 h-12 w-full"
            placeholder="Buscar técnico..."
            data-testid="search-technicians"
          />
        </div>

        {/* Store Filter */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          <Storefront size={18} className="text-[#A1A1AA] flex-shrink-0" />
          <button
            onClick={() => setFilterStore("")}
            className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors ${
              !filterStore
                ? "bg-[#00E676] text-black font-medium"
                : "bg-[#1A1A1A] text-white border border-white/10"
            }`}
          >
            Todos
          </button>
          {stores.map((store) => (
            <button
              key={store.id}
              onClick={() => setFilterStore(store.id)}
              className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors ${
                filterStore === store.id
                  ? "bg-[#00E676] text-black font-medium"
                  : "bg-[#1A1A1A] text-white border border-white/10"
              }`}
            >
              {store.name}
            </button>
          ))}
        </div>

        {/* Technicians List */}
        {filteredTechs.length === 0 ? (
          <div className="card p-8 text-center">
            <User size={48} className="mx-auto text-[#A1A1AA] mb-4" />
            <p className="text-[#A1A1AA] mb-4">Nenhum técnico cadastrado</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredTechs.map((tech) => (
              <div
                key={tech.id}
                className={`card p-4 sm:p-5 transition-all ${tech.active === false ? "opacity-50 grayscale-[50%]" : ""}`}
                data-testid={`technician-${tech.id}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                      <User size={24} className="text-blue-500 sm:w-6 sm:h-6" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-bold text-base sm:text-lg truncate flex items-center flex-wrap gap-2">
                        {tech.name}
                        {tech.active === false && (
                          <span className="text-[10px] uppercase font-bold bg-red-500/20 text-red-500 px-2 py-0.5 rounded border border-red-500/20">
                            Desativado
                          </span>
                        )}
                      </h3>
                      {tech.cpf && (
                        <div className="flex items-center gap-1.5 mt-1 text-xs text-[#A1A1AA]">
                          <IdentificationCard size={14} className="shrink-0" />
                          <span className="font-mono truncate">{tech.cpf}</span>
                        </div>
                      )}
                      {tech.phone && (
                        <div className="flex items-center gap-1.5 mt-1 text-xs text-[#A1A1AA]">
                          <Phone size={14} className="shrink-0" />
                          <span className="truncate">{tech.phone}</span>
                        </div>
                      )}
                      {tech.store_name && (
                        <div className="flex items-center gap-1.5 mt-1.5 text-xs text-[#00E676]">
                          <Storefront size={14} className="shrink-0" />
                          <span className="truncate">{tech.store_name}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-2 shrink-0 border-t border-white/5 sm:border-0 pt-3 sm:pt-0">
                    <button
                      onClick={() => handleEdit(tech)}
                      className="w-10 h-10 sm:w-auto sm:px-4 rounded-lg bg-[#1A1A1A] border border-white/10 flex items-center justify-center hover:bg-[#222] transition-colors gap-2"
                      data-testid={`edit-tech-${tech.id}`}
                    >
                      <Pencil size={18} className="text-[#A1A1AA]" />
                      <span className="hidden sm:inline text-sm text-[#A1A1AA]">
                        Editar
                      </span>
                    </button>
                    <button
                      onClick={() => handleToggleActive(tech)}
                      className={`w-10 h-10 sm:w-auto sm:px-4 rounded-lg border flex items-center justify-center transition-colors gap-2 ${
                        tech.active !== false
                          ? "bg-red-500/10 border-red-500/20 hover:bg-red-500/20 text-red-500"
                          : "bg-blue-500/10 border-blue-500/20 hover:bg-blue-500/20 text-blue-500"
                      }`}
                    >
                      {tech.active !== false ? (
                        <>
                          <Prohibit size={18} />
                          <span className="hidden sm:inline text-sm">
                            Desativar
                          </span>
                        </>
                      ) : (
                        <>
                          <ArrowCounterClockwise size={18} />
                          <span className="hidden sm:inline text-sm">
                            Reativar
                          </span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* FAB */}
        <button
          onClick={() => {
            setEditingTech(null);
            setFormData({ name: "", cpf: "", phone: "", store_id: "" });
            setShowForm(true);
          }}
          className="fixed bottom-24 right-4 w-14 h-14 bg-[#00E676] rounded-full flex items-center justify-center shadow-[0_0_15px_rgba(0,230,118,0.3)] hover:bg-[#00C853] hover:scale-105 transition-all z-20"
          data-testid="add-technician-button"
        >
          <Plus size={28} weight="bold" className="text-black" />
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-[#121212] rounded-xl p-5 sm:p-6 w-full max-w-md border border-white/10 shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-lg">
                {editingTech ? "Editar Técnico" : "Novo Técnico"}
              </h3>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingTech(null);
                }}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#1A1A1A] transition-colors"
              >
                <X size={20} className="text-[#A1A1AA]" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-xs text-[#A1A1AA] block mb-1.5 ml-1">
                  Nome *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="input-field w-full"
                  placeholder="Nome completo"
                  required
                  data-testid="tech-name-input"
                />
              </div>

              <div>
                <label className="text-xs text-[#A1A1AA] block mb-1.5 ml-1">
                  CPF
                </label>
                <input
                  type="text"
                  value={formData.cpf}
                  onChange={(e) =>
                    setFormData({ ...formData, cpf: e.target.value })
                  }
                  className="input-field w-full font-mono text-sm"
                  placeholder="000.000.000-00"
                  data-testid="tech-cpf-input"
                />
              </div>

              <div>
                <label className="text-xs text-[#A1A1AA] block mb-1.5 ml-1">
                  Telefone
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  className="input-field w-full"
                  placeholder="(00) 00000-0000"
                  data-testid="tech-phone-input"
                />
              </div>

              <div>
                <label className="text-xs text-[#A1A1AA] block mb-1.5 ml-1">
                  Loja
                </label>
                <select
                  value={formData.store_id}
                  onChange={(e) =>
                    setFormData({ ...formData, store_id: e.target.value })
                  }
                  className="input-field w-full"
                  data-testid="tech-store-select"
                >
                  <option value="">Sem loja vinculada</option>
                  {stores.map((store) => (
                    <option key={store.id} value={store.id}>
                      {store.name}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="submit"
                className="btn-primary w-full mt-6 py-3"
                data-testid="save-tech-button"
              >
                {editingTech ? "Salvar Alterações" : "Cadastrar Técnico"}
              </button>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
